import { BackspaceIcon, ReplyIcon } from '@heroicons/react/solid'
import classnames from 'classnames'
import { useEffect } from 'react'

import { getStatuses } from '../../lib/statuses'
import { localeAwareUpperCase } from '../../lib/words'
import { Key } from './Key'

type Props = {
  onChar: (value: string) => void
  onDelete: () => void
  onEnter: () => void
  solution: string
  guesses: string[]
  isRevealing?: boolean
  hasGameEnded: boolean
  isKeyboardSwapped: boolean
}

export const Keyboard = ({
  onChar,
  onDelete,
  onEnter,
  solution,
  guesses,
  isRevealing,
  hasGameEnded,
  isKeyboardSwapped,
}: Props) => {
  const charStatuses = getStatuses(solution, guesses)

  const onClick = (value: string) => {
    if (value === 'ENTER') {
      onEnter()
    } else if (value === 'DELETE') {
      onDelete()
    } else {
      onChar(value)
    }
  }

  const validLetters = 'AĀBCČDEĒFGĢHIĪJKĶLĻMNŅOPRSŠTUŪVZŽ'
  const hasDiacritic = 'ACEGIKLNSUZ'

  useEffect(() => {
    let diacritic: boolean = false

    const listener = (e: KeyboardEvent) => {
      if (e.code === 'Enter') {
        onEnter()
      } else if (e.code === 'Backspace') {
        onDelete()
      } else {
        const key = localeAwareUpperCase(e.key)

        if (key.length === 1 && validLetters.includes(key)) {
          if (diacritic && hasDiacritic.includes(key)) {
            onChar(validLetters.charAt(validLetters.indexOf(key) + 1))
            diacritic = false
          } else {
            onChar(key)
          }
        }

        if (['Quote', 'Backquote'].includes(e.code)) {
          diacritic = true
        }
      }
    }
    window.addEventListener('keyup', listener)
    return () => {
      window.removeEventListener('keyup', listener)
    }
  }, [onEnter, onDelete, onChar])

  const mapRow = (
    row: Array<string>,
    index: number,
    { length }: Array<Array<string>>
  ) => {
    let keyRow = []
    keyRow.push([
      row.map((key: string) => (
        <Key
          value={key}
          key={key}
          onClick={onClick}
          status={charStatuses[key]}
          isRevealing={isRevealing}
        />
      )),
    ])

    if (index === length - 1) {
      keyRow = [
        <Key value="DELETE" key="DELETE" onClick={onClick}>
          <BackspaceIcon className="mx-2 h-6 w-6" />
        </Key>,
        ...keyRow,
        <Key value="ENTER" key="ENTER" onClick={onClick}>
          <ReplyIcon className="mx-2 h-6 w-6 scale-y-[-1]" />
        </Key>,
      ]

      if (isKeyboardSwapped) {
        keyRow.reverse()
      }
    }

    return (
      <div className="flex justify-center space-x-1" key={`row-${index}`}>
        {keyRow}
      </div>
    )
  }

  return (
    <div
      className={classnames('w-full max-w-xl space-y-1 self-center px-1', {
        'pointer-events-none': hasGameEnded,
      })}
    >
      {[
        ['E', 'Ē', 'R', 'T', 'U', 'Ū', 'I', 'Ī', 'O', 'P', 'L', 'Ļ'],
        ['A', 'Ā', 'S', 'Š', 'D', 'F', 'G', 'Ģ', 'H', 'J', 'K', 'Ķ'],
        ['Z', 'Ž', 'C', 'Č', 'V', 'B', 'N', 'Ņ', 'M'],
      ].map(mapRow)}
    </div>
  )
}
